import { useQuery } from '@tanstack/react-query';
import { getUserProfile } from '@/lib/api';
import { auth } from '@/lib/firebase';
import { useEffect, useState } from 'react';
import * as Sentry from "@sentry/nextjs";
import { moengageLogin, moengageUpdateUserAttribute } from '@/lib/moengage';

export const useUserProfile = () => {
  const [isAuthReady, setIsAuthReady] = useState(false);

  useEffect(() => {
    const unsubscribe = auth?.onAuthStateChanged((user) => {
      setIsAuthReady(!!user);
    });
    return () => unsubscribe?.();
  }, []);

  const query = useQuery({
    queryKey: ['userProfile'],
    queryFn: getUserProfile,
    enabled: isAuthReady,
    retry: false,
    // Poll every 1 minute (60000ms) if user is not premium
    // This will automatically stop when isPremiumPlan becomes true
    refetchInterval: (query) => {
      // If user has premium plan, stop polling
      if (query.state.data?.user?.isPremiumPlan) {
        return false;
      }
      // Otherwise, poll every 1 minute
      return 60000;
    },
    refetchIntervalInBackground: false, // Don't poll when tab is not active
  });

  // Sync essential profile data to localStorage when it changes
  useEffect(() => {
    if (query.data?.user) {
      const user = query.data.user;

      // MoEngage User Identification
      if (user._id) {
        moengageLogin(user._id);

        if (user.email) moengageUpdateUserAttribute('email', user.email);
        if (user.phoneNumber) moengageUpdateUserAttribute('mobile', user.phoneNumber);
        if (user.fullName) moengageUpdateUserAttribute('name', user.fullName);
      }

      // Sentry User Identification
      Sentry?.setUser({
        id: user._id,
        email: user.email || undefined,
        username: user.fullName || undefined,
      });
    }
  }, [query.data]);

  return query;
};
